import React from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import CclButton from "../cclButtons/cclButton";
import ReplayIcon from "@mui/icons-material/Replay";

export interface CclTextOrSelectOption {
    value: string | number;
    label: string;
}

interface CclTextOrSelectSearchBarProps {
    initialTextSearchTerm: string;
    textSearchLabel: string;
    textFieldError?: string;
    selectLabel: string;
    selectOptions: CclTextOrSelectOption[];
    initialSelectValue?: string | number;
    executeSearch: (textTerm: string, selectValue: string | number) => void | Promise<void>;
    showResetButton?: boolean;
    clearSearchTrigger?: () => void;
}

const CclTextOrSelectSearchBar: React.FC<CclTextOrSelectSearchBarProps> = (props) => {
    const uid = React.useId();
    const selectLabelId = `${uid}-select-label`;
    const selectId = `${uid}-select`;
    const textFieldId = `${uid}-text-field`;

    const [lastFieldChanged, setLastFieldChanged] = React.useState<"text" | "select">("text");
    const [textSearchString, setTextSearchString] = React.useState<string>(
        props.initialTextSearchTerm ?? ""
    );
    const [selectValue, setSelectValue] = React.useState<string | number>(
        props.initialSelectValue ?? ""
    );

    React.useEffect(() => {
        setTextSearchString(props.initialTextSearchTerm ?? "");
    }, [props.initialTextSearchTerm]);

    React.useEffect(() => {
        setSelectValue(props.initialSelectValue ?? "");
    }, [props.initialSelectValue]);

    const fieldSx = {
        flex: "1 1 0",
        minWidth: 0,
    };

    const searchInputSx = {
        ...fieldSx,
        "& .MuiOutlinedInput-input": {
            paddingTop: "8px!important",
            paddingBottom: "8px!important",
        },
        "& .MuiInputLabel-root": {
            marginTop: "-8px!important",
        },
        "& .MuiInputLabel-shrink": {
            marginTop: "auto!important",
        },
    };

    const enableSearchButton = (): boolean => {
        if (lastFieldChanged === "text") return textSearchString !== "";
        return selectValue !== "" && selectValue !== undefined;
    };

    const executeSearch = () => {
        if (lastFieldChanged === "text" && textSearchString !== "") {
            props.executeSearch(textSearchString, "");
            setSelectValue("");
        } else if (lastFieldChanged === "select" && selectValue !== "" && selectValue !== undefined) {
            props.executeSearch("", selectValue);
            setTextSearchString("");
        }
    };

    const handleKeyPress = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            executeSearch();
        }
    };

    const clearSearchArguments = () => {
        setTextSearchString("");
        setSelectValue("");
        if (props.clearSearchTrigger) props.clearSearchTrigger();
    };

    return (
        <Box
            gap={2}
            sx={{
                p: 2,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            }}
        >
            <FormControl sx={fieldSx}>
                <InputLabel
                    id={selectLabelId}
                    sx={{ marginTop: "-8px", "&.MuiInputLabel-shrink": { marginTop: "auto" } }}
                >
                    {props.selectLabel ?? "Select"}
                </InputLabel>
                <Select
                    labelId={selectLabelId}
                    id={selectId}
                    value={selectValue}
                    label={props.selectLabel ?? "Select"}
                    onChange={(e: SelectChangeEvent<string | number>) => {
                        setLastFieldChanged("select");
                        setSelectValue(e.target.value);
                    }}
                    sx={{
                        "& .MuiOutlinedInput-input": {
                            paddingTop: "8px!important",
                            paddingBottom: "8px!important",
                        },
                    }}
                >
                    {props.selectOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography sx={{ color: "text.secondary" }}>Or</Typography>
            <TextField
                value={textSearchString}
                sx={searchInputSx}
                id={textFieldId}
                label={props.textSearchLabel ?? "Search"}
                variant="outlined"
                fullWidth
                error={!!props.textFieldError}
                helperText={props.textFieldError}
                onChange={(e) => {
                    e.preventDefault();
                    setLastFieldChanged("text");
                    setTextSearchString(e.target.value);
                }}
                onKeyDown={handleKeyPress}
            />
            {props.showResetButton && (
                <CclButton
                    mode="secondary"
                    sx={{
                        borderRadius: "100%",
                        maxWidth: "15px",
                        minWidth: "10px",
                        pl: "20px",
                        pr: "20px",
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        gap: "6px",
                        padding: "8px 4px",
                    }}
                    aria-label="Reset search"
                    onClick={clearSearchArguments}
                >
                    <ReplayIcon fontSize="small" />
                </CclButton>
            )}
            <CclButton
                onClick={executeSearch}
                aria-label="Search"
                size="small"
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                disabled={!enableSearchButton()}
                mode="primary"
            >
                Search
            </CclButton>
        </Box>
    );
};

export default CclTextOrSelectSearchBar;
